import { sharedApiClient } from "../../../api/ApiService";

// TSP API client — built on the same shared axios instance the rest of the
// app already uses (base URL, auth header interceptor, 401 handling), per
// the plan from Phase 1. Endpoints live under /api/tsp/... on the backend;
// the shared client's baseURL already ends in /api/, so paths here are
// relative ("tsp/...").
const TspApiService = {};

TspApiService.getMyTspAccess = (token) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return sharedApiClient({ url: "tsp/me", method: "GET", headers });
};

TspApiService.listTeams = (status) => {
  return sharedApiClient({ url: "tsp/teams", method: "GET", params: status ? { status } : undefined });
};

TspApiService.getTeam = (teamId) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}`, method: "GET" });
};

TspApiService.createTeam = (data) => {
  return sharedApiClient({ url: "tsp/teams", method: "POST", data });
};

TspApiService.updateTeam = (teamId, data) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}`, method: "PUT", data });
};

TspApiService.setTeamStatus = (teamId, status) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}/status`, method: "PUT", data: { status } });
};

TspApiService.listTeamMembers = (teamId) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}/members`, method: "GET" });
};

TspApiService.addTeamMember = (teamId, data) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}/members`, method: "POST", data });
};

// Backs the "Add Member" typeahead — looks up existing ICC users by name or
// phone so admins never have to know a numeric user id.
TspApiService.searchTeamUsers = (q) => {
  return sharedApiClient({ url: "tsp/teams/users/search", method: "GET", params: { q } });
};

TspApiService.removeTeamMember = (teamId, userId) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}/members/${userId}`, method: "DELETE" });
};

TspApiService.assignTeamLeader = (teamId, userId) => {
  return sharedApiClient({ url: `tsp/teams/${teamId}/leader/${userId}`, method: "PUT" });
};

// ---- Journeys ---------------------------------------------------------------
// Every call resolves to the API envelope ({ status, data, meta? }); a failure
// rejects with the axios error (error.response.data.message is the server's text).

TspApiService.listJourneys = (params) => {
  return sharedApiClient({ url: "tsp/journeys", method: "GET", params });
};

// The Journey List: one row per journey place (same filters / paging as listJourneys).
TspApiService.listJourneyPlaces = (params) => sharedApiClient({ url: "tsp/journeys/places", method: "GET", params });

TspApiService.getJourneySummary = () => {
  return sharedApiClient({ url: "tsp/journeys/summary", method: "GET" });
};

TspApiService.getJourneyFormOptions = () => {
  return sharedApiClient({ url: "tsp/journeys/form-options", method: "GET" });
};

// Team Leader typeahead: ICC users with TSP access, [{ id, name, phone_hint }].
TspApiService.searchJourneyLeaders = (q) => {
  return sharedApiClient({ url: "tsp/journeys/leaders", method: "GET", params: { q } });
};

TspApiService.getJourney = (journeyId) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}`, method: "GET" });
};

TspApiService.createJourney = (data) => {
  return sharedApiClient({ url: "tsp/journeys", method: "POST", data });
};

TspApiService.updateJourney = (journeyId, data) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}`, method: "PUT", data });
};

// action: "cancel" (needs { reason }). A journey starts when created and completes by itself.
TspApiService.transitionJourney = (journeyId, action, data) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/${action}`, method: "POST", data });
};

// "Finish Journey" at the last stop (no body): completes the journey once the
// last stop of its route has its visit (journeys.complete).
TspApiService.finishJourney = (journeyId) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/finish`, method: "POST" });
};

// Update status of a place on a journey (live | completed | not_visited)
TspApiService.updatePlaceStatus = (journeyId, data) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/place-status`, method: "POST", data });
};


// ---- Coverage map -----------------------------------------------------------
// Points are saved city-visit coordinates; the server applies journey scope.

// A map point -> { latitude, longitude, status: RESOLVED | PARTIAL | NOT_FOUND,
// address, place (TSP state / district / town ids), areas, source }. Needs
// journeys.manage (it exists for "Create Journey" from the map); 503 when the
// geocoding provider cannot be reached.
TspApiService.reverseGeocode = ({ latitude, longitude }) => {
  return sharedApiClient({ url: "tsp/geocoding/reverse", method: "GET", params: { latitude, longitude } });
};

TspApiService.getMapCoverage = (params) => {
  return sharedApiClient({ url: "tsp/map/coverage", method: "GET", params });
};

TspApiService.getMapJourneyRoute = (journeyId) => {
  return sharedApiClient({ url: `tsp/map/journeys/${journeyId}`, method: "GET" });
};

// The coverage map's location search: states / districts / towns matching `q`,
// then where a picked one is ({ type: "state" | "district" | "city", id }).
TspApiService.searchMapPlaces = (q) => {
  return sharedApiClient({ url: "tsp/map/places", method: "GET", params: { q } });
};

TspApiService.locateMapPlace = ({ type, id }) => {
  return sharedApiClient({ url: "tsp/map/places/locate", method: "GET", params: { type, id } });
};

// ---- Route planning ---------------------------------------------------------

TspApiService.getRoute = (journeyId) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route`, method: "GET" });
};

TspApiService.getRouteHistory = (journeyId, params) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/history`, method: "GET", params });
};

// cities, in the desired order: [{ location_id } | { state_id, district_id, city_id }
// | { state_id, district_id, area_name } | { state_id, city_name }] (see utils/tspPlace.js).
TspApiService.saveRoute = (journeyId, cities) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route`, method: "PUT", data: { cities } });
};

// One stop at a time (Edit Journey): `stop` is a route item plus its optional
// planned_date / areas / latitude / longitude; `position` is 1-based (default: the end).
TspApiService.addRouteCity = (journeyId, stop, position) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/cities`, method: "POST", data: position ? { ...stop, position } : stop });
};

// A stop's own details: { planned_date } and / or { areas }.
TspApiService.updateRouteCity = (journeyId, cityId, changes) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/cities/${cityId}`, method: "PATCH", data: changes });
};

// "Update Location": the stop's point looked up by the server from its place
// (nothing saved) -> { city_id, label, latitude, longitude, approximate }.
TspApiService.locateRouteCity = (journeyId, cityId) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/cities/${cityId}/locate`, method: "GET" });
};

// Saves a stop's { latitude, longitude } (must be inside the stop's state) -> the route.
TspApiService.updateRouteCityLocation = (journeyId, cityId, point) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/cities/${cityId}/location`, method: "PUT", data: point });
};

TspApiService.removeRouteCity = (journeyId, cityId) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/cities/${cityId}`, method: "DELETE" });
};

// city_ids: the route's journey-city ids in their new order.
TspApiService.reorderRoute = (journeyId, cityIds) => {
  return sharedApiClient({ url: `tsp/journeys/${journeyId}/route/order`, method: "PUT", data: { city_ids: cityIds } });
};

// ---- City visits ------------------------------------------------------------

TspApiService.listCityVisits = (params) => {
  return sharedApiClient({ url: "tsp/city-visits", method: "GET", params });
};

TspApiService.getCityVisit = (id) => {
  return sharedApiClient({ url: `tsp/city-visits/${id}`, method: "GET" });
};

TspApiService.createCityVisit = (data) => {
  return sharedApiClient({ url: "tsp/city-visits", method: "POST", data });
};
// Journey and city are fixed; only the visit's own fields change (IN_PROGRESS journeys only).
TspApiService.updateCityVisit = (id, data) => {
  return sharedApiClient({ url: `tsp/city-visits/${id}`, method: "PUT", data });
};

TspApiService.deleteCityVisit = (id) => {
  return sharedApiClient({ url: `tsp/city-visits/${id}`, method: "DELETE" });
};


// ---- Reference data (states, and the places routes have used) ----------------

TspApiService.listStates = () => {
  return sharedApiClient({ url: "tsp/locations/states", method: "GET" });
};

// The State -> District -> City hierarchy behind the route pickers (all of India).
TspApiService.listDistricts = (stateId) => {
  return sharedApiClient({ url: "tsp/locations/districts", method: "GET", params: { state_id: stateId } });
};

TspApiService.listCities = (districtId, params) => {
  return sharedApiClient({ url: "tsp/locations/cities", method: "GET", params: { district_id: districtId, ...params } });
};

TspApiService.listLocations = (params) => {
  return sharedApiClient({ url: "tsp/locations", method: "GET", params });
};

// ---- Book distribution -------------------------------------------------------

TspApiService.getBookCatalog = () => sharedApiClient({ url: "tsp/book-distributions/catalog", method: "GET" });
TspApiService.listBookDistributions = (params) => sharedApiClient({ url: "tsp/book-distributions", method: "GET", params });
TspApiService.getBookDistributionSummary = (params) => sharedApiClient({ url: "tsp/book-distributions/summary", method: "GET", params });
TspApiService.createBookDistribution = (data) => sharedApiClient({ url: "tsp/book-distributions", method: "POST", data });
TspApiService.createBookDistributionBatch = (journeyId, data) => sharedApiClient({ url: `tsp/journeys/${journeyId}/book-distributions/batch`, method: "POST", data });
TspApiService.updateBookDistribution = (id, data) => sharedApiClient({ url: `tsp/book-distributions/${id}`, method: "PUT", data });
TspApiService.deleteBookDistribution = (id) => sharedApiClient({ url: `tsp/book-distributions/${id}`, method: "DELETE" });

// ---- Public response ----------------------------------------------------------

TspApiService.listPublicResponses = (params) => sharedApiClient({ url: "tsp/public-responses", method: "GET", params });
TspApiService.getPublicResponseSummary = (params) => sharedApiClient({ url: "tsp/public-responses/summary", method: "GET", params });
TspApiService.createPublicResponse = (data) => sharedApiClient({ url: "tsp/public-responses", method: "POST", data });
TspApiService.updatePublicResponse = (id, data) => sharedApiClient({ url: `tsp/public-responses/${id}`, method: "PUT", data });
TspApiService.deletePublicResponse = (id) => sharedApiClient({ url: `tsp/public-responses/${id}`, method: "DELETE" });

// ---- Daily reports --------------------------------------------------------------

TspApiService.listDailyReports = (params) => sharedApiClient({ url: "tsp/daily-reports", method: "GET", params });
TspApiService.listPendingDailyReports = (params) => sharedApiClient({ url: "tsp/daily-reports/pending", method: "GET", params });
TspApiService.createDailyReport = (data) => sharedApiClient({ url: "tsp/daily-reports", method: "POST", data });
TspApiService.updateDailyReport = (id, data) => sharedApiClient({ url: `tsp/daily-reports/${id}`, method: "PUT", data });

// ---- Contacts / Accommodation / Parking / Prasadam (shared shape) --------------
// Each is { list, create, remove } against its own flat resource path.

const locationResource = (path) => ({
  list: (params) => sharedApiClient({ url: `tsp/${path}`, method: "GET", params }),
  create: (data) => sharedApiClient({ url: `tsp/${path}`, method: "POST", data }),
  update: (id, data) => sharedApiClient({ url: `tsp/${path}/${id}`, method: "PUT", data }),
  remove: (id) => sharedApiClient({ url: `tsp/${path}/${id}`, method: "DELETE" }),
});

TspApiService.contacts = locationResource("contacts");
TspApiService.accommodations = locationResource("accommodations");
TspApiService.parking = locationResource("parking");
TspApiService.prasadam = locationResource("prasadam");

// ---- Media / Advice / Knowledge (Phase 4) -------------------------------------
// Media uploads are multipart (the file is validated by content on the server);
// axios sets the multipart boundary itself when given a FormData.

TspApiService.media = {
  list: (params) => sharedApiClient({ url: "tsp/media", method: "GET", params }),
  // The shared client's 60 s timeout is too short for a 200 MB video on a slow connection.
  upload: (formData) => sharedApiClient({ url: "tsp/media/upload", method: "POST", data: formData, headers: { "Content-Type": "multipart/form-data" }, timeout: 10 * 60 * 1000 }),
  remove: (id) => sharedApiClient({ url: `tsp/media/${id}`, method: "DELETE" }),
};

TspApiService.advice = {
  list: (params) => sharedApiClient({ url: "tsp/advice", method: "GET", params }),
  get: (id) => sharedApiClient({ url: `tsp/advice/${id}`, method: "GET" }),
  create: (data) => sharedApiClient({ url: "tsp/advice", method: "POST", data }),
  update: (id, data) => sharedApiClient({ url: `tsp/advice/${id}`, method: "PUT", data }),
  remove: (id) => sharedApiClient({ url: `tsp/advice/${id}`, method: "DELETE" }),
};

// One list (city cards) and one detail (the whole city view in a single response).
TspApiService.knowledge = {
  list: (params) => sharedApiClient({ url: "tsp/knowledge", method: "GET", params }),
  get: (locationId) => sharedApiClient({ url: `tsp/knowledge/${locationId}`, method: "GET" }),
};

// ---- Dashboard / Your Stats (read-only aggregates) -----------------------

TspApiService.getDashboard = () => sharedApiClient({ url: "tsp/dashboard", method: "GET" });

// journeyId omitted -> the caller's own running (or most recently touched) journey.
// "Your Stats": overall aggregate summary of all journeys of the caller's temple(s).
TspApiService.getTempleStats = () => sharedApiClient({ url: "tsp/stats/temple", method: "GET" });

TspApiService.getTemplePlaces = () => sharedApiClient({ url: "tsp/stats/places", method: "GET" });
TspApiService.getPlaceStats = (placeId) => sharedApiClient({ url: `tsp/stats/places/${placeId}`, method: "GET" });

TspApiService.getJourneyStats = (journeyId) => sharedApiClient({ url: "tsp/stats", method: "GET", params: journeyId ? { journey_id: journeyId } : undefined });

// Team leaderboard (Live Ranking): every team in the caller's scope with
// books / cities / response for month, year and all time, each with `prev`.
// Live Ranking: temples ranked by the number of yatras in `year` (GET /tsp/ranking/temples).
TspApiService.getTempleRanking = (year) => sharedApiClient({ url: "tsp/ranking/temples", method: "GET", params: year ? { year } : undefined });

// ---- Forum (Phase 5) ---------------------------------------------------------

TspApiService.forum = {
  listCategories: () => sharedApiClient({ url: "tsp/forum/categories", method: "GET" }),
  listTopics: (params) => sharedApiClient({ url: "tsp/forum/topics", method: "GET", params }),
  getTopic: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}`, method: "GET" }),
  createTopic: (data) => sharedApiClient({ url: "tsp/forum/topics", method: "POST", data }),
  updateTopic: (id, data) => sharedApiClient({ url: `tsp/forum/topics/${id}`, method: "PUT", data }),
  deleteTopic: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}`, method: "DELETE" }),
  recordView: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/view`, method: "POST" }),
  toggleLike: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/like`, method: "POST" }),
  togglePin: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/pin`, method: "POST" }),
  reportTopic: (id, reason) => sharedApiClient({ url: `tsp/forum/topics/${id}/report`, method: "POST", data: { reason } }),
  clearTopicReport: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/report`, method: "DELETE" }),
  subscribe: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/subscribe`, method: "POST" }),
  unsubscribe: (id) => sharedApiClient({ url: `tsp/forum/topics/${id}/subscribe`, method: "DELETE" }),
  listSubscriptions: () => sharedApiClient({ url: "tsp/forum/subscriptions", method: "GET" }),
  listReplies: (topicId, params) => sharedApiClient({ url: `tsp/forum/topics/${topicId}/replies`, method: "GET", params }),
  createReply: (topicId, data) => sharedApiClient({ url: `tsp/forum/topics/${topicId}/replies`, method: "POST", data }),
  updateReply: (id, data) => sharedApiClient({ url: `tsp/forum/replies/${id}`, method: "PUT", data }),
  deleteReply: (id) => sharedApiClient({ url: `tsp/forum/replies/${id}`, method: "DELETE" }),
  toggleHelpful: (id) => sharedApiClient({ url: `tsp/forum/replies/${id}/helpful`, method: "POST" }),
  toggleBestAnswer: (id) => sharedApiClient({ url: `tsp/forum/replies/${id}/best-answer`, method: "POST" }),
  reportReply: (id, reason) => sharedApiClient({ url: `tsp/forum/replies/${id}/report`, method: "POST", data: { reason } }),
  clearReplyReport: (id) => sharedApiClient({ url: `tsp/forum/replies/${id}/report`, method: "DELETE" }),
};

// ---- Stories / Blogs (Phase 5) -----------------------------------------------

TspApiService.stories = {
  list: (params) => sharedApiClient({ url: "tsp/stories", method: "GET", params }),
  get: (id) => sharedApiClient({ url: `tsp/stories/${id}`, method: "GET" }),
  create: (data) => sharedApiClient({ url: "tsp/stories", method: "POST", data }),
  update: (id, data) => sharedApiClient({ url: `tsp/stories/${id}`, method: "PUT", data }),
  remove: (id) => sharedApiClient({ url: `tsp/stories/${id}`, method: "DELETE" }),
  toggleLike: (id) => sharedApiClient({ url: `tsp/stories/${id}/like`, method: "POST" }),
  listComments: (id, params) => sharedApiClient({ url: `tsp/stories/${id}/comments`, method: "GET", params }),
  addComment: (id, data) => sharedApiClient({ url: `tsp/stories/${id}/comments`, method: "POST", data }),
  updateComment: (commentId, data) => sharedApiClient({ url: `tsp/story-comments/${commentId}`, method: "PUT", data }),
  deleteComment: (commentId) => sharedApiClient({ url: `tsp/story-comments/${commentId}`, method: "DELETE" }),
};

// ---- Reports (Phase 5) --------------------------------------------------------
// `format` ("csv" | "xlsx" | "pdf") triggers a file download instead of JSON —
// callers pass it through TspApiService.downloadReport, not .get*Report.

const REPORT_PATHS = {
  journeys: "journeys",
  teams: "teams",
  states: "states",
  cities: "cities",
  distribution: "distribution",
  books: "books",
  publicResponse: "public-response",
  contacts: "contacts",
  accommodation: "accommodation",
  parking: "parking",
  media: "media",
  activity: "activity",
};

// The Journey List as an Excel file (every journey matching the list's filters).
// Returns a Blob, like reports.download.
TspApiService.exportJourneys = (params) => sharedApiClient({ url: "tsp/journeys/export", method: "GET", params, responseType: "blob" });

TspApiService.reports = {
  get: (key, params) => sharedApiClient({ url: `tsp/reports/${REPORT_PATHS[key]}`, method: "GET", params }),
  // Returns a Blob (responseType override) so the caller can trigger a save.
  download: (key, format, params) =>
    sharedApiClient({ url: `tsp/reports/${REPORT_PATHS[key]}`, method: "GET", params: { ...params, format }, responseType: "blob" }),
};

// ---- Global Search (Phase 5) --------------------------------------------------

TspApiService.search = (params) => sharedApiClient({ url: "tsp/search", method: "GET", params });

// ---- Live GPS tracking of a journey -----------------------------------------------
// The tracked user is always the logged-in caller (never sent); the server
// checks journey scope and only accepts positions while the journey is IN_PROGRESS.

TspApiService.tracking = {
  // { journey, can_share, me, trackers: [...], config, server_time }
  getStatus: (journeyId) => sharedApiClient({ url: `tsp/journeys/${journeyId}/tracking`, method: "GET" }),
  // Movement history from a cursor: { data: [{ id, session_id, user_id, lat, lng, accuracy, t }], meta: { next_after, has_more } }
  getPath: (journeyId, params) => sharedApiClient({ url: `tsp/journeys/${journeyId}/tracking/points`, method: "GET", params }),
  start: (journeyId) => sharedApiClient({ url: `tsp/journeys/${journeyId}/tracking/start`, method: "POST", data: {} }),
  // points: [{ lat, lng, accuracy, speed?, heading?, t }] — an empty list is a heartbeat.
  // gps: this device's own GPS state, "OK" | "NO_SIGNAL" | "UNAVAILABLE".
  sendPoints: (journeyId, sessionId, points, gps) =>
    sharedApiClient({ url: `tsp/journeys/${journeyId}/tracking/points`, method: "POST", data: { session_id: sessionId, points, gps }, timeout: 20000 }),
  stop: (journeyId, data) => sharedApiClient({ url: `tsp/journeys/${journeyId}/tracking/stop`, method: "POST", data: data || {} }),
};

// ---- Global User Live Location Tracking -----------------------------------------
TspApiService.userLocations = {
  ping: (data) => sharedApiClient({ url: "tsp/user-locations/ping", method: "POST", data, timeout: 15000 }),
  listLive: () => sharedApiClient({ url: "tsp/user-locations/live", method: "GET" }),
};

export default TspApiService;
